import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  WorkflowContainer,
  WorkflowContainerMember,
  WorkflowContainerStatus,
  User,
  UserRole,
} from "../../types";
import axios from "axios";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { apiService } from "../../services/apiService";
import { formatCurrencyByCode } from "../../utils/currency";
import { formatProjectDisplayName } from "../../utils/projectCode";
import {
  parseProjectNumericValue,
  resolveProjectFinanceInputs,
} from "../../utils/projectFinance";

interface ProjectDetailModalProps {
  project: WorkflowContainer | null;
  isOpen: boolean;
  onClose: () => void;
  workflowInstanceName?: string; // Optional: workflow instance name to display
  workflowInstanceId?: number;
  workflowContainerFormData?: Record<string, any>; // Optional: form data entered when starting workflow
}

export const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({
  project,
  isOpen,
  onClose,
  workflowInstanceName,
  workflowInstanceId,
  workflowContainerFormData,
}) => {
  const { t } = useTranslation(["projects", "common"]);
  const { getWorkflowLabel, isAdmin, isManager, user } = useAuth();

  // Company-specific terminology
  const workflowText = getWorkflowLabel();
  const canViewFinancials = isAdmin() || isManager();
  const canVisitorViewFinancialDetails = (() => {
    if (user?.role !== UserRole.VISITOR || !user.settingsJson) {
      return false;
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      return Boolean(settings?.workflowPermissions?.visitorFinancialAccess);
    } catch {
      return false;
    }
  })();

  const [members, setMembers] = useState<WorkflowContainerMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSource, setMemberSource] = useState<"container" | "instance">(
    "container"
  );

  useEffect(() => {
    if (isOpen && (project?.id || workflowInstanceId)) {
      loadMembers();
    }
  }, [project?.id, workflowInstanceId, isOpen]);

  const loadMembers = async () => {
    try {
      setLoadingMembers(true);
      if (workflowInstanceId) {
        const instanceData = await apiService.getWorkflowInstanceFormData(
          workflowInstanceId
        );
        const memberIds = instanceData.projectMembers || [];
        if (memberIds.length === 0) {
          setMembers([]);
          setMemberSource("instance");
          return;
        }

        const users = await apiService.getUsers(true);
        const userMap = new Map<number, User>(
          users.map((user) => [user.id, user])
        );
        const now = new Date().toISOString();

        const instanceMembers = memberIds
          .map((userId) => {
            const user = userMap.get(userId);
            if (!user) {
              return null;
            }
            return {
              id: userId,
              workflowContainerId: project?.id || 0,
              userId,
              role: "team_member",
              canStartWorkflow: false,
              addedAt: now,
              user: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                username: user.username,
                email: user.email,
              },
            } as WorkflowContainerMember;
          })
          .filter(
            (member): member is WorkflowContainerMember => member !== null
          );

        setMembers(instanceMembers);
        setMemberSource("instance");
        return;
      }

      const response = await axios.get("/api/workflow-container-members", {
        params: { workflowContainerId: project!.id },
      });
      if (response.data.success) {
        setMembers(response.data.data || []);
        setMemberSource("container");
      }
    } catch (error) {
      toast.error("Üyeler yüklenemedi");
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const getStatusColor = (status: WorkflowContainerStatus) => {
    switch (status) {
      case WorkflowContainerStatus.PLANNING:
        return "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200";
      case WorkflowContainerStatus.IN_PROGRESS:
        return "bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border border-blue-200";
      case WorkflowContainerStatus.COMPLETED:
        return "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200";
      case WorkflowContainerStatus.ON_HOLD:
        return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-800 border border-slate-200";
      case WorkflowContainerStatus.CANCELLED:
        return "bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200";
      default:
        return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-800 border border-slate-200";
    }
  };

  const getStatusDisplayName = (status: WorkflowContainerStatus): string => {
    switch (status) {
      case WorkflowContainerStatus.PLANNING:
        return t("projects:status.planning");
      case WorkflowContainerStatus.IN_PROGRESS:
        return t("dashboard:inProgress");
      case WorkflowContainerStatus.COMPLETED:
        return t("dashboard:completed");
      case WorkflowContainerStatus.ON_HOLD:
        return t("projects:status.onHold");
      case WorkflowContainerStatus.CANCELLED:
        return t("dashboard:cancelled");
      default:
        return status;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const projectFinanceInputs = resolveProjectFinanceInputs(
    workflowContainerFormData,
    project?.formElements,
  );

  const formatCurrency = (amount: unknown) => {
    const numericAmount = parseProjectNumericValue(amount);
    if (numericAmount === null) return "-";
    return formatCurrencyByCode(
      numericAmount,
      projectFinanceInputs.projectCurrency,
      { display: "suffix" },
    );
  };

  const getUserInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "?";
    const first = firstName?.charAt(0).toUpperCase() || "";
    const last = lastName?.charAt(0).toUpperCase() || "";
    return first + last;
  };

  // Helper function to get form field value from formElements or workflowContainerFormData
  const getFormValue = (fieldId: string): any => {
    // First check workflowContainerFormData (user submitted data)
    if (workflowContainerFormData?.[fieldId] !== undefined) {
      return workflowContainerFormData[fieldId];
    }
    // Then check formElements (template default values)
    const element = project?.formElements?.find((el) => el.id === fieldId);
    return element?.value;
  };

  const formatDetailValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return t("common:notProvided");
    }

    if (Array.isArray(value)) {
      const items = value
        .map((item) => String(item ?? "").trim())
        .filter(Boolean);
      return items.length > 0 ? items.join(", ") : t("common:notProvided");
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed === "[]" || trimmed === "{}") {
        return t("common:notProvided");
      }
      return trimmed;
    }

    return String(value);
  };

  // Get common fields
  const projectName =
    getFormValue("name") ||
    getFormValue("projectName") ||
    workflowInstanceName ||
    "-";
  const projectDescription =
    getFormValue("description") || getFormValue("projectDescription");
  const projectBudget = getFormValue("budget") || getFormValue("projectBudget");
  const projectClientName =
    getFormValue("clientName") || getFormValue("client");
  const projectType = getFormValue("type") || getFormValue("projectType");
  const projectProgress = getFormValue("progress");
  const projectStartDate = getFormValue("startDate");
  const projectEndDate = getFormValue("endDate");
  const projectDuration =
    getFormValue("estimatedDurationDays") || getFormValue("duration");
  const projectCountry = getFormValue("ulke") || getFormValue("country");
  const projectDisplayName = formatProjectDisplayName({
    name: projectName,
    projectTypeName: workflowInstanceName || projectType,
    startDate: project?.createdAt,
    country: projectCountry,
  });

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-2xl">🚀</span>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  {projectDisplayName || projectName}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t("projects:projectDetails")}
                </p>
              </div>
              <span
                className={`inline-flex px-4 py-2 text-sm font-semibold rounded-full ${getStatusColor(
                  project.status
                )}`}
              >
                {getStatusDisplayName(project.status)}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sol Kolon - Temel Bilgiler */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                📋 Temel Bilgiler
              </h3>

              {/* Proje Adı */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Proje Adı
                </label>
                <p className="text-base font-semibold text-gray-900">
                  {projectDisplayName || projectName}
                </p>
              </div>

              {/* İş Akışı / Proje Türü */}
              {workflowInstanceName && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                  <label className="block text-sm font-medium text-purple-700 mb-1 flex items-center">
                    <span className="mr-1">📋</span>
                    {workflowText}
                  </label>
                  <p className="text-base font-semibold text-purple-900">
                    {workflowInstanceName}
                  </p>
                </div>
              )}

              {/* Açıklama */}
              {projectDescription && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Açıklama
                  </label>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {projectDescription}
                  </p>
                </div>
              )}

              {/* Müşteri */}
              {projectClientName && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    👤 Müşteri
                  </label>
                  <p className="text-base font-semibold text-gray-900">
                    {projectClientName}
                  </p>
                </div>
              )}

              {/* Tip */}
              {projectType && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    🏷️ Proje Tipi
                  </label>
                  <p className="text-base font-semibold text-gray-900">
                    {projectType}
                  </p>
                </div>
              )}

              {/* İlerleme */}
              {projectProgress !== undefined && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    📈 İlerleme
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-300"
                        style={{ width: `${projectProgress}%` }}
                      />
                    </div>
                    <span className="text-base font-bold text-gray-900 min-w-[3rem] text-right">
                      {projectProgress}%
                    </span>
                  </div>
                </div>
              )}

              {/* Bütçe */}
              {canViewFinancials && projectBudget && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <label className="block text-sm font-medium text-green-700 mb-1 flex items-center">
                    <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                    Bütçe
                  </label>
                  <p className="text-2xl font-bold text-green-800">
                    {formatCurrency(projectBudget)}
                  </p>
                </div>
              )}

              {/* Diğer Detaylar */}
              {workflowContainerFormData &&
                Object.keys(workflowContainerFormData).length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                      <span className="mr-2">📝</span>
                      {t("projects:otherDetails")}
                    </h4>
                    <div className="space-y-2">
                      {(() => {
                        const normalizeFinancialToken = (value: unknown): string =>
                          String(value ?? "")
                            .toLocaleLowerCase("tr-TR")
                            .replace(/ı/g, "i")
                            .replace(/ö/g, "o")
                            .replace(/ü/g, "u")
                            .replace(/ş/g, "s")
                            .replace(/ğ/g, "g")
                            .replace(/ç/g, "c")
                            .replace(/[^a-z0-9_ ]/g, "");
                        const sensitiveFinancialIds = new Set([
                          "budget",
                          "projectbudget",
                          "projectcurrency",
                          "currency",
                          "currencycode",
                          "para_birimi",
                          "parabirimi",
                          "project_currency",
                        ]);
                        const isSensitiveFinancialKey = (key: string): boolean => {
                          const normalized = normalizeFinancialToken(key);
                          if (
                            normalized === "offer_amount" &&
                            canVisitorViewFinancialDetails
                          ) {
                            return false;
                          }
                          if (sensitiveFinancialIds.has(normalized)) {
                            return true;
                          }
                          return (
                            normalized.includes("para birimi") ||
                            normalized.includes("doviz") ||
                            normalized.includes("currency")
                          );
                        };
                        return Object.entries(workflowContainerFormData)
                        .filter(([key]) => {
                          // Filter out system fields and date fields (they are shown in Dates section)
                          return (
                            key !== "name" &&
                            key !== "warningThreshold" &&
                            key !== "progressCalculationMethod" &&
                            key !== "startDate" &&
                            key !== "endDate" &&
                            key !== "estimatedDurationDays" &&
                            key !== "elementCurrencies" &&
                            key !== "element_currencies" &&
                            (canViewFinancials ||
                              canVisitorViewFinancialDetails ||
                              !isSensitiveFinancialKey(key)) &&
                            !key.startsWith("element_")
                          );
                        })
                        .map(([key, value]) => (
                          <div
                            key={key}
                            className="bg-white rounded p-2 border border-blue-100"
                          >
                            <div className="text-xs text-gray-600 font-medium mb-1">
                              {key}
                            </div>
                            <div className="text-sm text-gray-900">
                              {formatDetailValue(value)}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}
            </div>

            {/* Sağ Kolon - Tarihler ve Ekip */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                📅 Tarihler ve Ekip
              </h3>

              {/* Tarihler */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      {t("projects:startDate")}
                    </label>
                    <p className="text-base font-semibold text-blue-900">
                      {formatDate(projectStartDate)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      {t("projects:endDate")}
                    </label>
                    <p className="text-base font-semibold text-blue-900">
                      {formatDate(projectEndDate)}
                    </p>
                  </div>
                </div>

                {projectDuration && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <label className="block text-sm font-medium text-blue-700 mb-1">
                      ⏱️ {t("projects:estimatedDurationDays")}
                    </label>
                    <p className="text-base font-semibold text-blue-900">
                      {projectDuration} {t("projects:days")}
                    </p>
                  </div>
                )}
              </div>

              {/* Proje Yöneticisi */}
              {project.managerName && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <label className="block text-sm font-medium text-purple-700 mb-2 flex items-center">
                    <UserCircleIcon className="w-5 h-5 mr-1" />
                    Proje Yöneticisi
                  </label>
                  <p className="text-lg font-bold text-purple-900">
                    {project.managerName}
                  </p>
                </div>
              )}

              {/* Proje Üyeleri */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center justify-between">
                  <span className="flex items-center">
                    <UserIcon className="w-5 h-5 mr-1" />
                    👥 Proje Üyeleri
                  </span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {members.length} üye
                  </span>
                </label>

                {loadingMembers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : members.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                            {getUserInitials(
                              member.user?.firstName,
                              member.user?.lastName
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {member.user?.firstName} {member.user?.lastName}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              @{member.user?.username}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          {memberSource === "container" ? (
                            <>
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {member.role === "project_manager"
                                  ? member.user?.role === UserRole.PROJECT_MANAGER
                                    ? t("projects:roles.projectManager")
                                    : t("projects:manager")
                                  : t("projects:roles.teamMember")}
                              </span>
                              {member.canStartWorkflow ? (
                                <CheckCircleIcon
                                  className="w-5 h-5 text-green-500"
                                  title="Workflow başlatabilir"
                                />
                              ) : (
                                <XCircleIcon
                                  className="w-5 h-5 text-gray-300"
                                  title="Workflow başlatamaz"
                                />
                              )}
                            </>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                              {t("projects:roles.teamMember")}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <UserIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Henüz üye eklenmemiş</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
